/* ==========================================================================
   LOUREL — Mega-menu hover-intent controller (desktop)
   --------------------------------------------------------------------------
   The theme's own <header-menu> component (assets/header-menu.js) does the
   heavy lifting: it computes panel heights, manages aria-expanded, swaps
   content and shows the featured image. What it gets wrong is *when* to
   open and close: it opened on raw pointerenter and closed instantly on
   pointerleave, so crossing the gap between a nav link and its panel closed
   the menu, and tiny pointer movements made it flicker.

   The native pointer bindings were removed in blocks/_header-menu.liquid
   (focus/blur are kept there for keyboard access). This controller re-adds
   pointer handling with hover intent:

   - open after a short dwell (OPEN_DELAY) so skimming the bar does nothing
   - switch between items after SWITCH_DELAY without closing in between
   - close only after CLOSE_DELAY, so the pointer can cross the gap between
     the trigger and the panel (the panel is a DOM child of the <li>, so
     re-entering it cancels the pending close)
   - Escape and page scroll close immediately
   - panels are equalized to the tallest panel's natural height, so
     switching between menus never makes the panel jump (see equalize())

   Listeners are delegated on `document`, so the header can be re-rendered
   (theme editor, view transitions) without rebinding.
   ========================================================================== */
(function () {
  'use strict';

  var OPEN_DELAY = 100; // ms dwell before opening
  var SWITCH_DELAY = 80; // ms when another menu is already open
  var CLOSE_DELAY = 200; // ms grace period to travel from trigger into panel

  var openTimer = 0;
  var closeTimer = 0;

  function isDesktopMenu(li) {
    var hc = li.closest('header-component');
    return (
      !!hc &&
      hc.dataset.menuStyle === 'menu' &&
      window.matchMedia('(min-width: 750px)').matches
    );
  }

  /** Top-level menu <li> (never inside a submenu, never the More/overflow items). */
  function topLevelItem(node) {
    if (!node || !(node instanceof Element)) return null;
    var li = node.closest('.menu-list__list-item');
    if (!li) return null;
    if (li.slot === 'more' || li.slot === 'overflow') return null; // native handles overflow
    if (li.closest('.menu-list__submenu')) return null;
    return li;
  }

  /** The upgraded <header-menu> component owning this item, or null. */
  function menuComponent(li) {
    var hm = li.closest('header-menu');
    return hm && typeof hm.activate === 'function' && typeof hm.deactivate === 'function' ? hm : null;
  }

  /** The currently expanded top-level link inside this header-menu, or null. */
  function expandedLink(hm) {
    return hm.querySelector(
      '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link[aria-expanded="true"]'
    );
  }

  function submenus(hm) {
    return hm.querySelectorAll('.menu-list__list-item:not([slot="overflow"]) > .menu-list__submenu');
  }

  /**
   * Equalize the open panel to the tallest panel's natural height so
   * WOMEN / MEN / NIGHTS all open to the same height. Inactive submenus have
   * content-visibility:auto with a placeholder intrinsic size, so each is
   * measured with data-active temporarily set (they stay visibility:hidden,
   * nothing flashes). The open submenu also gets a matching min-height so
   * the equalized area is a real hover target (it is a child of the <li>,
   * so hovering it keeps the menu open). Runs synchronously right after
   * activate(), which computes its own heights synchronously too.
   */
  function equalize(hm, li) {
    var hc = li.closest('header-component');
    if (!hc) return;
    var current = parseFloat(hc.style.getPropertyValue('--submenu-height')) || 0;
    if (current <= 0) return; // nothing open
    var fullOpen = parseFloat(hc.style.getPropertyValue('--full-open-header-height')) || 0;
    var headerVisible = fullOpen - current;

    var maxHeight = current;
    submenus(hm).forEach(function (sub) {
      var wasActive = sub.hasAttribute('data-active');
      if (!wasActive) sub.setAttribute('data-active', '');
      var h = sub.offsetHeight; // forces layout with content-visibility active
      if (h > 0) maxHeight = Math.max(maxHeight, h);
      if (!wasActive) sub.removeAttribute('data-active');
    });

    if (maxHeight > current + 1) {
      var active = li.querySelector(':scope > .menu-list__submenu');
      if (active) active.style.minHeight = maxHeight + 'px';
      hc.style.setProperty('--submenu-height', maxHeight + 'px');
      hc.style.setProperty('--full-open-header-height', maxHeight + headerVisible + 'px');
    }
  }

  /** Open an item via the component, then equalize the panel height. */
  function openItem(hm, li) {
    // Clear stale equalizer min-heights so the component measures natural
    // content height (matters after viewport resizes).
    submenus(hm).forEach(function (sub) {
      sub.style.minHeight = '';
    });
    hm.activate({ target: li });
    equalize(hm, li);
  }

  document.addEventListener(
    'pointerover',
    function (event) {
      var li = topLevelItem(event.target);
      if (!li) return;
      if (event.relatedTarget && li.contains(event.relatedTarget)) return; // move within item/panel
      if (!isDesktopMenu(li)) return;
      var hm = menuComponent(li);
      if (!hm) return;

      clearTimeout(closeTimer);
      clearTimeout(openTimer);

      var open = expandedLink(hm);
      if (open && li.contains(open)) return; // already open (e.g. re-entered the panel)

      openTimer = setTimeout(function () {
        openItem(hm, li);
      }, open ? SWITCH_DELAY : OPEN_DELAY);
    },
    true
  );

  document.addEventListener(
    'pointerout',
    function (event) {
      var li = topLevelItem(event.target);
      if (!li) return;
      if (event.relatedTarget && li.contains(event.relatedTarget)) return; // move within item/panel
      if (!isDesktopMenu(li)) return;
      var hm = menuComponent(li);
      if (!hm) return;

      clearTimeout(openTimer);

      var open = expandedLink(hm);
      if (!open || !li.contains(open)) return; // this item isn't the open one

      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        var still = expandedLink(hm);
        if (still && li.contains(still)) {
          hm.deactivate({ target: li, relatedTarget: null });
        }
      }, CLOSE_DELAY);
    },
    true
  );

  function closeNow() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    var menus = document.querySelectorAll('header-menu');
    for (var i = 0; i < menus.length; i++) {
      var hm = menus[i];
      if (typeof hm.deactivate !== 'function') continue;
      var open = expandedLink(hm);
      if (open) {
        hm.deactivate({
          target: open.closest('.menu-list__list-item') || open,
          relatedTarget: null,
        });
      }
    }
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeNow();
  });
  window.addEventListener('scroll', closeNow, { passive: true });

  // Keyboard focus opens via the component's native on:focus binding;
  // equalize that panel too (after the focus handler has run).
  document.addEventListener('focusin', function (event) {
    var li = topLevelItem(event.target);
    if (!li || !isDesktopMenu(li)) return;
    var hm = menuComponent(li);
    if (!hm) return;
    setTimeout(function () {
      equalize(hm, li);
    }, 0);
  });
})();
